import { z } from 'zod'

/**
 * Fehlerprotokoll (ADR 0022): errors the app notices itself – client exceptions, UI freezes, navigation loops,
 * failed API calls and server-side 500s/job failures. Grouped by fingerprint, shown in Einstellungen and exportable
 * as text for a bug report.
 */

export const ErrorSource = z.enum(['client', 'desktop', 'api', 'worker'])
export type ErrorSource = z.infer<typeof ErrorSource>

export const ErrorKind = z.enum(['exception', 'unhandled', 'render', 'freeze', 'nav_loop', 'http', 'job'])
export type ErrorKind = z.infer<typeof ErrorKind>

/** max. reports per POST (the client batches) */
export const MAX_ERROR_BATCH = 20

export const ErrorReportInput = z.object({
  source: z.enum(['client', 'desktop']),
  kind: ErrorKind,
  message: z.string().min(1).max(2000),
  stack: z.string().max(8000).optional(),
  /** route, user agent, app version, breadcrumbs (last navigations/clicks), request path … */
  context: z.record(z.string(), z.unknown()).default({}),
  /** when the client saw it (offline reports arrive later) */
  at: z.iso.datetime({ offset: true }).optional(),
})
export type ErrorReportInput = z.infer<typeof ErrorReportInput>

export const ErrorReportBatch = z.object({ reports: z.array(ErrorReportInput).min(1).max(MAX_ERROR_BATCH) })

export const ErrorReport = z.object({
  id: z.uuid(),
  source: ErrorSource,
  kind: ErrorKind,
  message: z.string(),
  stack: z.string().nullable(),
  context: z.record(z.string(), z.unknown()),
  count: z.number().int(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  resolvedAt: z.string().nullable(),
})
export type ErrorReport = z.infer<typeof ErrorReport>

const when = (s: string) =>
  new Date(s).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', dateStyle: 'short', timeStyle: 'medium' })

/**
 * Plain-text export of the Fehlerprotokoll ("Kopieren" in Einstellungen) – everything needed to reproduce a bug:
 * kind, message, how often/when, context (route, version, breadcrumbs) and the stack.
 */
export function formatErrorReports(reports: ErrorReport[], meta: { exportedAt?: string; app?: string } = {}) {
  const head = [
    `# Fehlerprotokoll – Voluro Second Brain`,
    `Exportiert: ${when(meta.exportedAt ?? new Date().toISOString())}${meta.app ? ` · ${meta.app}` : ''}`,
    `Einträge: ${reports.length}`,
  ]
  const body = reports.map((r, i) => {
    const { breadcrumbs, ...ctx } = r.context
    const lines = [
      `## ${i + 1}. [${r.source}/${r.kind}] ${r.message}`,
      `${r.count}× · zuerst ${when(r.firstSeen)} · zuletzt ${when(r.lastSeen)}${r.resolvedAt ? ' · erledigt' : ''}`,
    ]
    for (const [k, v] of Object.entries(ctx))
      lines.push(`- ${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    if (Array.isArray(breadcrumbs) && breadcrumbs.length)
      lines.push('', 'Verlauf (älteste zuerst):', ...breadcrumbs.map((b) => `  ${String(b)}`))
    if (r.stack) lines.push('', '```', r.stack.trim(), '```')
    return lines.join('\n')
  })
  return [head.join('\n'), ...body].join('\n\n')
}
